import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { ChevronRight, Clock } from "lucide-react";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export const PageHero = ({ title, updated, intro, icon: Icon, eyebrow = "Help & Policies", children }) => (
  <header className="relative overflow-hidden border-b border-border/60 bg-gradient-to-br from-primary/10 via-muted/40 to-background">
    <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />
    <div className="absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-secondary/10 blur-3xl" aria-hidden="true" />
    <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
      <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-muted-foreground mb-8">
        <Link to="/" className="hover:text-primary">Home</Link>
        <ChevronRight className="w-3 h-3" aria-hidden="true" />
        <Link to="/customer-service" className="hover:text-primary">Help & Policies</Link>
        <ChevronRight className="w-3 h-3" aria-hidden="true" />
        <span className="text-foreground/70">{title}</span>
      </nav>
      <div className="flex items-start gap-5 animate-fade-up">
        {Icon && (
          <span className="hidden sm:flex shrink-0 items-center justify-center h-14 w-14 rounded-2xl bg-primary/10 text-primary ring-1 ring-primary/20">
            <Icon className="w-7 h-7" aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-primary">{eyebrow}</span>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-[1.05] mt-1.5">{title}</h1>
          {updated && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-3">
              <Clock className="w-3.5 h-3.5" aria-hidden="true" /> Last updated: {updated}
            </div>
          )}
          {intro && <p className="text-[15px] leading-relaxed text-muted-foreground mt-4 max-w-2xl">{intro}</p>}
          {children}
        </div>
      </div>
    </div>
  </header>
);

export const LegalLayout = ({
  title, updated = "June 2026", intro, icon: Icon, eyebrow = "Help & Policies",
  sections = [], children, testid,
}) => {
  useEffect(() => { document.title = `${title} — Xurray`; }, [title]);

  return (
    <Layout hideAd>
      <div data-testid={testid}>
        <PageHero title={title} updated={updated} intro={intro} icon={Icon} eyebrow={eyebrow} />

        {/* Body */}
        <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {sections.length > 0 ? (
            <div className="lg:grid lg:grid-cols-[220px_1fr] lg:gap-14">
              <aside className="hidden lg:block">
                <nav aria-label="On this page" className="sticky top-24">
                  <p className="text-[11px] font-bold tracking-[0.15em] uppercase text-muted-foreground mb-3">On this page</p>
                  <ul className="space-y-2 text-sm border-l border-border">
                    {sections.map((s, i) => (
                      <li key={i}>
                        <a href={`#${slug(s.heading)}`} className="block -ml-px border-l-2 border-transparent pl-4 py-0.5 text-muted-foreground transition-colors hover:border-primary hover:text-primary">
                          {s.heading}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </aside>
              <div className="space-y-10 min-w-0">
                {children && <div className="mb-2">{children}</div>}
                {sections.map((s, i) => (
                  <section key={i} id={slug(s.heading)} className="scroll-mt-24 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }}>
                    <h2 className="font-display font-bold text-xl mb-3 flex items-baseline gap-3">
                      <span className="text-sm font-black text-primary tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                      {s.heading}
                    </h2>
                    <div className="space-y-3 leading-relaxed text-[15px] text-foreground/90 pl-0 sm:pl-9">{s.body}</div>
                  </section>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-6 leading-relaxed text-[15px] text-foreground/90">{children}</div>
          )}
        </main>
      </div>
    </Layout>
  );
};
